import type { Inline } from "./blocks.ts";
import { DOC_CODE_PATTERN, DOC_CODE_RE } from "./doc-types.ts";
import { SchemaError } from "./errors.ts";

export type Mark = "bold" | "italic" | "code";
export type InlineToken =
  | { t: "text"; text: string; mark?: Mark }
  | { t: "link"; text: string; href: string }
  | { t: "doc"; text: string; code: string }
  | { t: "placeholder"; label: string };

// While scanning, an escaped character ("\*") is replaced by a Unicode noncharacter
// (U+FDD0 + its index in ESCAPABLE), so the token regex never sees it. Noncharacters
// found in the input are removed first; they never occur in real text.
const ESCAPABLE = "\\*`[]";
const SENTINEL = 0xfdd0;
const SENTINEL_RE = /[\uFDD0-\uFDD4]/g;
const ESCAPE_RE = /\\([\\*`[\]])/g;

const toSentinel = (ch: string) => String.fromCharCode(SENTINEL + ESCAPABLE.indexOf(ch));
const restore = (s: string) => s.replace(SENTINEL_RE, (c) => ESCAPABLE.charAt(c.charCodeAt(0) - SENTINEL));

// Characters an href may not contain: whitespace, C0 controls and DEL, ( ) [ ] < > " ` \ and the sentinels.
const HREF_EXCLUDED = '\\s\\u0000-\\u001F\\u007F()[\\]<>"`\\\\\\uFDD0-\\uFDD4';
const HREF_BODY = `(?:https?:\\/\\/|mailto:)[^${HREF_EXCLUDED}]+`;
const HREF_RE = new RegExp(`^${HREF_BODY}$`);
const HREF_EXCLUDED_RE = new RegExp(`[${HREF_EXCLUDED}]`, "g");
const SCHEME_RE = /^(https?|mailto):/i;

// encodeURIComponent leaves ( ) unescaped; every other excluded character it encodes as UTF-8.
const percentEncode = (ch: string) => {
  const e = encodeURIComponent(ch);
  return e !== ch ? e : `%${ch.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`;
};

// Alternatives are tried left to right at each position, like legacy runs():
// placeholder, link / doc link, then **bold**, `code`, *italic* (legacy order).
const TOKEN_RE = new RegExp(
  [
    /\[\[([^[\]]+)\]\]/.source,
    `\\[([^[\\]]+)\\]\\((${HREF_BODY}|doc:${DOC_CODE_PATTERN})\\)`,
    /\*\*([\s\S]+?)\*\*/.source,
    /`([\s\S]+?)`/.source,
    /\*([\s\S]+?)\*/.source,
  ].join("|"),
  "g",
);

/** True for hrefs a link may carry: http(s) or mailto, no spaces, brackets, quotes or backslashes. */
export function isSafeHref(href: string): boolean {
  return HREF_RE.test(href);
}

/**
 * Repair a pasted or typed href so that isSafeHref accepts it, or return null when it cannot be a link.
 * Trims, accepts only http:, https: and mailto: (any case; the scheme is lowercased) and percent-encodes
 * every character isSafeHref excludes. Never decodes, so existing %XX sequences are kept as they are.
 */
export function toSafeHref(href: string): string | null {
  const trimmed = href.trim();
  const scheme = SCHEME_RE.exec(trimmed);
  if (!scheme) return null;
  const rest = trimmed.slice(scheme[0].length).replace(HREF_EXCLUDED_RE, percentEncode);
  const out = `${scheme[0].toLowerCase()}${rest}`;
  return isSafeHref(out) ? out : null;
}

function pushText(out: InlineToken[], text: string, mark?: Mark): void {
  if (!text) return;
  const prev = out[out.length - 1];
  if (prev?.t === "text" && prev.mark === mark) {
    prev.text += text;
    return;
  }
  out.push(mark ? { t: "text", text, mark } : { t: "text", text });
}

function pushLink(out: InlineToken[], text: string, href: string): void {
  const prev = out[out.length - 1];
  if (prev?.t === "link" && prev.href === href) {
    prev.text += text;
    return;
  }
  out.push({ t: "link", text, href });
}

/** Parse inline markup into tokens. Adjacent text with the same mark (and adjacent links to the same href) are merged. */
export function parseInline(input: Inline): InlineToken[] {
  const src = String(input)
    .replace(SENTINEL_RE, "")
    .replace(ESCAPE_RE, (_m, ch: string) => toSentinel(ch));
  const out: InlineToken[] = [];
  const re = new RegExp(TOKEN_RE.source, "g");
  let last = 0;
  for (let m = re.exec(src); m !== null; m = re.exec(src)) {
    pushText(out, restore(src.slice(last, m.index)));
    const [, label, linkText, target, bold, code, italic] = m;
    if (label !== undefined) out.push({ t: "placeholder", label: restore(label) });
    else if (linkText !== undefined && target !== undefined) {
      if (target.startsWith("doc:")) out.push({ t: "doc", text: restore(linkText), code: target.slice(4) });
      else pushLink(out, restore(linkText), target);
    } else if (bold !== undefined) pushText(out, restore(bold), "bold");
    else if (code !== undefined) pushText(out, restore(code), "code");
    else pushText(out, restore(italic ?? ""), "italic");
    last = re.lastIndex;
  }
  pushText(out, restore(src.slice(last)));
  return out;
}

// A serialised string is a list of segments. Escapable characters inside text start out
// escaped; serializeInline then drops every escape whose removal does not change the parse.
type Segment = { s: string; escapable: boolean; escaped: boolean };

const raw = (s: string): Segment => ({ s, escapable: false, escaped: false });

function textSegments(text: string): Segment[] {
  return [...text.replace(SENTINEL_RE, "")].map((ch) =>
    ESCAPABLE.includes(ch) ? { s: ch, escapable: true, escaped: true } : raw(ch),
  );
}

const render = (segs: Segment[]) => segs.map((x) => (x.escaped ? `\\${x.s}` : x.s)).join("");

function tokenSegments(token: InlineToken): Segment[] {
  switch (token.t) {
    case "text": {
      const delim = token.mark === "bold" ? "**" : token.mark === "italic" ? "*" : token.mark === "code" ? "`" : "";
      return [raw(delim), ...textSegments(token.text), raw(delim)];
    }
    case "link":
      if (!token.text) throw new SchemaError("link text is empty");
      if (!isSafeHref(token.href)) throw new SchemaError(`unsupported link target "${token.href}"`);
      return [raw("["), ...textSegments(token.text), raw(`](${token.href})`)];
    case "doc":
      if (!token.text) throw new SchemaError("doc link text is empty");
      if (!DOC_CODE_RE.test(token.code)) throw new SchemaError(`invalid document code "${token.code}"`);
      return [raw("["), ...textSegments(token.text), raw(`](doc:${token.code})`)];
    case "placeholder":
      if (!token.label) throw new SchemaError("placeholder label is empty");
      return [raw("[["), ...textSegments(token.label), raw("]]")];
  }
}

// Upper bound on characters re-parsed while dropping escapes one by one, shared by every
// string of one top-level call (tiptapToBlocks, canonical, or a direct serializeInline /
// canonicalInline call). Past it, the remaining escapes stay: the output is still exact and
// canonical, just more escaped, and very long markup-heavy documents cannot make a save slow.
const MINIMIZE_BUDGET = 1_000_000;

/** Re-parse allowance for one top-level call; passed down explicitly, never module state. */
export type MinimizeBudget = { left: number };
export const minimizeBudget = (): MinimizeBudget => ({ left: MINIMIZE_BUDGET });

/** Serialise tokens to canonical inline markup: escapes only where the parse would otherwise change. */
export function serializeInline(tokens: InlineToken[]): Inline {
  return serializeInlineWithin(tokens, minimizeBudget());
}

/** serializeInline drawing on a budget shared with the other strings of the same call. */
export function serializeInlineWithin(tokens: InlineToken[], budget: MinimizeBudget): Inline {
  const segs = tokens.flatMap((t) => (t.t === "text" && !t.text ? [] : tokenSegments(t)));
  const escapable = segs.filter((x) => x.escapable);
  const expected = JSON.stringify(parseInline(render(segs)));
  const unchanged = () => JSON.stringify(parseInline(render(segs))) === expected;
  // Most text needs no escapes at all.
  for (const seg of escapable) seg.escaped = false;
  if (unchanged()) return render(segs);
  for (const seg of escapable) seg.escaped = true;
  // Otherwise drop escapes left to right, keeping each one whose removal changes the parse.
  const cost = render(segs).length;
  for (const seg of escapable) {
    if (budget.left < cost) break;
    budget.left -= cost;
    seg.escaped = false;
    if (!unchanged()) seg.escaped = true;
  }
  return render(segs);
}

/** Canonical form of an inline string. */
export function canonicalInline(s: Inline): Inline {
  return canonicalInlineWithin(s, minimizeBudget());
}

/** canonicalInline drawing on a budget shared with the other strings of the same call. */
export function canonicalInlineWithin(s: Inline, budget: MinimizeBudget): Inline {
  return serializeInlineWithin(parseInline(s), budget);
}
